import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../App";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Car, Loader2, RefreshCw, Search, CheckCircle2, AlertCircle, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin tool to backfill make/model/year on existing quote requests
 * that have a rego but no vehicle details. Calls the CarJam API per
 * request via the backend admin endpoints.
 */
export function AdminVehicleBackfillPanel() {
  const { token } = useAuth();
  const authConfig = { headers: { Authorization: `Bearer ${token}` } };
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [perRowState, setPerRowState] = useState({}); // { [request_id]: {status, message, vehicle} }
  const [bulkRunning, setBulkRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/quote-requests/missing-vehicle-info`,
        authConfig,
      );
      setRows(res.data.requests || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load list.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Load list when the panel is expanded
  useEffect(() => {
    if (open && rows.length === 0 && !loading) load();
  }, [open, rows.length, loading, load]);

  const lookupOne = async (requestId) => {
    setPerRowState((s) => ({ ...s, [requestId]: { status: "loading" } }));
    try {
      const res = await axios.post(
        `${API}/admin/quote-requests/${requestId}/backfill-vehicle`,
        {},
        { ...authConfig, timeout: 20000 },
      );
      const r = res.data;
      if (r.status === "ok") {
        toast.success(
          `Filled: ${r.vehicle_info?.make || "?"} ${r.vehicle_info?.model || ""} ${r.vehicle_info?.year || ""}`,
        );
        setPerRowState((s) => ({
          ...s,
          [requestId]: { status: "ok", vehicle: r.vehicle_info },
        }));
        // Remove this row from the list after a short delay
        setTimeout(() => {
          setRows((prev) => prev.filter((x) => x.request_id !== requestId));
          setTotal((t) => Math.max(0, t - 1));
        }, 1500);
      } else {
        const msg = r.message || `Lookup returned: ${r.status}`;
        toast.error(msg);
        setPerRowState((s) => ({
          ...s,
          [requestId]: { status: r.status, message: msg },
        }));
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Lookup failed.";
      toast.error(msg);
      setPerRowState((s) => ({
        ...s,
        [requestId]: { status: "error", message: msg },
      }));
    }
  };

  const runBulk = async () => {
    if (!window.confirm(`Run CarJam lookup for all ${total} requests? This may take ~${Math.ceil(total * 1.5)} seconds.`)) {
      return;
    }
    setBulkRunning(true);
    try {
      const res = await axios.post(
        `${API}/admin/quote-requests/backfill-vehicle-all`,
        {},
        { ...authConfig, timeout: 5 * 60 * 1000 },
      );
      const ok = (res.data.results || []).filter((r) => r.status === "ok").length;
      const failed = (res.data.results || []).length - ok;
      toast.success(
        `Backfill complete · ${ok} filled · ${failed} could not be looked up`,
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk backfill failed.");
    } finally {
      setBulkRunning(false);
    }
  };

  return (
    <Card className="glass-card border-amber-300 dark:border-amber-800">
      <CardHeader className="pb-3">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center justify-between w-full text-left"
          data-testid="vehicle-backfill-toggle"
        >
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Car className="h-4 w-4 text-amber-600" />
              Vehicle info backfill
              {total > 0 && (
                <Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/40 ml-1">
                  {total} pending
                </Badge>
              )}
            </CardTitle>
            <CardDescription className="text-xs mt-1">
              Look up missing make / model / year on requests that already have
              a rego, via CarJam.
            </CardDescription>
          </div>
          {open ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </button>
      </CardHeader>

      {open && (
        <CardContent className="space-y-3">
          {/* Action row */}
          <div className="flex flex-wrap items-center gap-2 pb-2 border-b">
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
              data-testid="vehicle-backfill-refresh"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={runBulk}
              disabled={loading || bulkRunning || total === 0}
              className="bg-amber-600 text-white hover:bg-amber-700"
              data-testid="vehicle-backfill-run-all"
            >
              {bulkRunning ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <Search className="h-3.5 w-3.5 mr-1.5" />
              )}
              {bulkRunning ? "Looking up…" : `Look up all ${total}`}
            </Button>
            <p className="text-[11px] text-muted-foreground ml-auto">
              Idempotent — re-runs only fill empty fields. Customer-entered
              values are never overwritten.
            </p>
          </div>

          {/* List */}
          {loading && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-amber-600" />
            </div>
          )}
          {!loading && rows.length === 0 && (
            <p className="text-center text-sm text-muted-foreground py-6">
              No requests need vehicle backfill.
            </p>
          )}
          {!loading && rows.length > 0 && (
            <div className="space-y-1.5 max-h-[420px] overflow-y-auto">
              {rows.map((r) => {
                const state = perRowState[r.request_id];
                return (
                  <div
                    key={r.request_id}
                    className="grid grid-cols-[120px_1fr_auto] gap-3 items-center px-3 py-2 rounded border bg-muted/20 text-sm"
                    data-testid={`vehicle-backfill-row-${r.request_id}`}
                  >
                    <div>
                      <p className="font-medium text-sm">
                        {r.request_number || `#${r.request_id}`}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {r.suburb || "—"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs">
                        Rego{" "}
                        <span className="font-semibold tracking-wide">
                          {r.rego}
                        </span>
                        {state?.vehicle && (
                          <>
                            {" · "}
                            <span className="text-green-600 dark:text-green-400 font-medium">
                              {state.vehicle.make} {state.vehicle.model}{" "}
                              {state.vehicle.year}
                            </span>
                          </>
                        )}
                      </p>
                      {state?.message && state.status !== "ok" && (
                        <p className="text-[11px] text-red-500 mt-0.5 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {state.message}
                        </p>
                      )}
                    </div>
                    <div>
                      {state?.status === "ok" ? (
                        <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400 font-medium px-2 py-1">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          Filled
                        </span>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => lookupOne(r.request_id)}
                          disabled={state?.status === "loading"}
                          className="h-7 text-xs"
                          data-testid={`vehicle-backfill-btn-${r.request_id}`}
                        >
                          {state?.status === "loading" ? (
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          ) : (
                            <Search className="h-3 w-3 mr-1" />
                          )}
                          Look up
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

export default AdminVehicleBackfillPanel;
